import React from 'react';
import { Logo, Button } from '../ui';
import { ClipboardListIcon, Users2Icon, BarChart4Icon, SparklesIcon, CheckIcon, CheckCircle2Icon, PrinterIcon, ArrowLeftIcon, HotelIcon, MapIcon, BriefcaseIcon, UsersIcon, MicIcon, Wand2Icon, LayoutDashboardIcon, GraduationCapIcon, HeartPulseIcon, NetworkIcon, MessageSquareQuoteIcon, PieChartIcon } from 'lucide-react';
export function SalesSheet() {
  const handlePrint = () => window.print();
  return <div className="min-h-screen bg-slate-100 py-10 print:bg-white print:py-0" data-id="element-7205">
      {/* Toolbar — hidden in print */}
      <div className="max-w-[850px] mx-auto px-6 mb-6 flex items-center justify-between print:hidden" data-id="element-7206">
        <a href="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-7207">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-7208" /> Back to site
        </a>
        <Button variant="primary" size="sm" onClick={handlePrint} data-id="element-7209">
          <PrinterIcon className="w-4 h-4 mr-2" data-id="element-7210" /> Download / Print PDF
        </Button>
      </div>

      {/* Sheet — letter sized */}
      <article className="bg-white max-w-[850px] mx-auto shadow-2xl print:shadow-none rounded-lg print:rounded-none overflow-hidden" style={{
      minHeight: '1100px'
    }} data-id="element-7211">
        {/* Header band */}
        <header className="bg-brand-navy text-white px-10 py-8 relative overflow-hidden" data-id="element-7212">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-teal via-brand-coral to-brand-teal" data-id="element-7213"></div>
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-brand-teal/10 rounded-full blur-3xl pointer-events-none" data-id="element-7214"></div>

          <div className="relative flex items-start justify-between" data-id="element-7215">
            <div data-id="element-7216">
              <div className="font-bold text-3xl tracking-tight flex items-start mb-2" data-id="element-7217">
                <span className="text-white" data-id="element-7218">Offsite</span>
                <span className="text-brand-coral text-xl leading-none font-black relative -top-0.5" data-id="element-7219">
                  +
                </span>
              </div>
              <p className="text-slate-300 text-sm font-medium" data-id="element-7220">
                The engagement layer for meetings, retreats, and group stays.
              </p>
            </div>
            <div className="text-right" data-id="element-7221">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1" data-id="element-7222">
                Sales Sheet
              </div>
              <div className="text-xs text-slate-300 font-mono" data-id="element-7223">
                v1.0 · 2026
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="px-10 py-8 space-y-8" data-id="element-7224">
          {/* Headline */}
          <section data-id="element-7225">
            <h1 className="text-3xl font-bold text-brand-navy leading-tight mb-3 text-balance" data-id="element-7226">
              Turn any offsite into a structured, measurable experience.
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed" data-id="element-7227">
              Offsite+ helps planners and hospitality partners design, run, and
              measure better offsites with AI-generated planning, attendee
              intake, learning pathways, group matching, wellness preferences,
              and client-ready reporting.
            </p>
          </section>

          {/* Problem / Solution stripe */}
          <section className="grid grid-cols-2 gap-4" data-id="element-7228">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-id="element-7229">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brand-coral mb-2" data-id="element-7230">
                The Problem
              </div>
              <p className="text-xs text-slate-700 leading-relaxed" data-id="element-7231">
                Most offsites are expensive but hard to measure. Attendees
                arrive unprepared, networking is random, preferences are missed,
                and the client leaves without a clear view of what worked.
              </p>
            </div>
            <div className="bg-brand-navy text-white rounded-xl p-5" data-id="element-7232">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brand-teal mb-2" data-id="element-7233">
                The Solution
              </div>
              <p className="text-xs text-slate-200 leading-relaxed" data-id="element-7234">
                Offsite+ adds the missing experience layer before, during, and
                after the event — so planners and hospitality partners deliver
                more value with less chaos.
              </p>
            </div>
          </section>

          {/* Before / During / After */}
          <section data-id="element-7235">
            <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-4" data-id="element-7236">
              The Experience Layer
            </h2>
            <div className="grid grid-cols-3 gap-3" data-id="element-7237">
              {[{
              label: 'Before',
              icon: <ClipboardListIcon className="w-4 h-4" data-id="element-7238" />,
              color: 'border-brand-teal bg-brand-teal/5 text-brand-teal',
              items: ['Attendee intake', 'Goals & preferences', 'Introductions', 'AI blueprint']
            }, {
              label: 'During',
              icon: <Users2Icon className="w-4 h-4" data-id="element-7239" />,
              color: 'border-brand-coral bg-brand-coral/5 text-brand-coral',
              items: ['Event hub', 'Agenda & prompts', 'Group matching', 'Activity selection']
            }, {
              label: 'After',
              icon: <BarChart4Icon className="w-4 h-4" data-id="element-7240" />,
              color: 'border-brand-navy bg-brand-navy/5 text-brand-navy',
              items: ['Surveys', 'Commitments', 'Analytics', 'Client report']
            }].map(stage => <div key={stage.label} className={`rounded-xl border-l-4 ${stage.color} bg-white border-t border-r border-b border-slate-200 p-4`} data-id="element-7241">
                  <div className={`flex items-center gap-2 mb-3 ${stage.color.split(' ')[2]}`} data-id="element-7242">
                    {stage.icon}
                    <span className="text-sm font-bold" data-id="element-7243">{stage.label}</span>
                  </div>
                  <ul className="space-y-1.5" data-id="element-7244">
                    {stage.items.map(item => <li key={item} className="text-xs text-slate-600 flex items-center gap-1.5" data-id="element-7245">
                        <div className="w-1 h-1 rounded-full bg-slate-400" data-id="element-7246"></div>
                        {item}
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </section>

          {/* Key features */}
          <section data-id="element-7247">
            <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-4" data-id="element-7248">
              Key Features
            </h2>
            <div className="grid grid-cols-4 gap-2" data-id="element-7249">
              {[{
              icon: <Wand2Icon className="w-3.5 h-3.5" data-id="element-7250" />,
              label: 'Free AI Designer'
            }, {
              icon: <ClipboardListIcon className="w-3.5 h-3.5" data-id="element-7251" />,
              label: 'Branded Intake'
            }, {
              icon: <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-7252" />,
              label: 'Event Hub'
            }, {
              icon: <GraduationCapIcon className="w-3.5 h-3.5" data-id="element-7253" />,
              label: 'Learning Pathways'
            }, {
              icon: <HeartPulseIcon className="w-3.5 h-3.5" data-id="element-7254" />,
              label: 'Wellness & Activities'
            }, {
              icon: <NetworkIcon className="w-3.5 h-3.5" data-id="element-7255" />,
              label: 'Group Matching'
            }, {
              icon: <MessageSquareQuoteIcon className="w-3.5 h-3.5" data-id="element-7256" />,
              label: 'Reflection Prompts'
            }, {
              icon: <PieChartIcon className="w-3.5 h-3.5" data-id="element-7257" />,
              label: 'Post-Event Report'
            }].map(f => <div key={f.label} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex items-center gap-2" data-id="element-7258">
                  <span className="text-brand-teal shrink-0" data-id="element-7259">{f.icon}</span>
                  <span className="text-[11px] font-semibold text-brand-navy leading-tight" data-id="element-7260">
                    {f.label}
                  </span>
                </div>)}
            </div>
          </section>

          {/* Intelligence Engine callout */}
          <section className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-5 flex items-start gap-4" data-id="element-7261">
            <div className="bg-brand-coral/10 p-2.5 rounded-lg shrink-0" data-id="element-7262">
              <SparklesIcon className="w-5 h-5 text-brand-coral" data-id="element-7263" />
            </div>
            <div data-id="element-7264">
              <h3 className="text-sm font-bold text-brand-navy mb-1" data-id="element-7265">
                Offsite+ Intelligence Engine
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed" data-id="element-7266">
                AI-powered learning pathways built from proven leadership,
                wellness, and executive-education frameworks.
              </p>
            </div>
          </section>

          {/* Pilot offer — hero block */}
          <section className="border-2 border-brand-coral rounded-xl overflow-hidden relative" data-id="element-7267">
            <div className="absolute top-3 right-3 bg-brand-coral text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-7268">
              Pilot Offer
            </div>
            <div className="bg-brand-coral/5 px-5 pt-5 pb-4" data-id="element-7269">
              <div className="flex items-baseline gap-3 mb-1" data-id="element-7270">
                <h3 className="text-lg font-bold text-brand-navy" data-id="element-7271">
                  Founding Partner Pilot
                </h3>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-7272">
                <span className="text-2xl font-black text-brand-navy" data-id="element-7273">
                  $1,500–$3,500
                </span>
                <span className="text-xs text-slate-500 font-medium" data-id="element-7274">
                  per event
                </span>
              </div>
            </div>
            <div className="px-5 py-4 bg-white" data-id="element-7275">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2" data-id="element-7276">
                Includes
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5" data-id="element-7277">
                {['AI offsite blueprint', 'Branded intake form', 'Event hub setup', 'Curated learning pathway', 'Group matching structure', 'Survey flow', 'Post-event report'].map(item => <div key={item} className="flex items-center gap-2 text-xs text-slate-700" data-id="element-7278">
                    <CheckCircle2Icon className="w-3.5 h-3.5 text-brand-teal shrink-0" data-id="element-7279" />
                    {item}
                  </div>)}
              </div>
            </div>
          </section>

          {/* Who it's for */}
          <section data-id="element-7280">
            <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3" data-id="element-7281">
              Who It's For
            </h2>
            <div className="grid grid-cols-5 gap-2" data-id="element-7282">
              {[{
              icon: <HotelIcon className="w-4 h-4" data-id="element-7283" />,
              label: 'Hotels & Resorts'
            }, {
              icon: <MapIcon className="w-4 h-4" data-id="element-7284" />,
              label: 'DMCs'
            }, {
              icon: <BriefcaseIcon className="w-4 h-4" data-id="element-7285" />,
              label: 'MICE Planners'
            }, {
              icon: <UsersIcon className="w-4 h-4" data-id="element-7286" />,
              label: 'Corporate HR'
            }, {
              icon: <MicIcon className="w-4 h-4" data-id="element-7287" />,
              label: 'Retreat Facilitators'
            }].map(a => <div key={a.label} className="border border-slate-200 rounded-lg p-3 flex flex-col items-center text-center gap-1.5 bg-white" data-id="element-7288">
                  <div className="text-brand-navy" data-id="element-7289">{a.icon}</div>
                  <div className="text-[10px] font-semibold text-slate-700 leading-tight" data-id="element-7290">
                    {a.label}
                  </div>
                </div>)}
            </div>
          </section>

          {/* CTAs */}
          <section className="bg-brand-navy rounded-xl px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4 relative overflow-hidden" data-id="element-7291">
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-coral/20 rounded-full blur-3xl pointer-events-none" data-id="element-7292"></div>
            <div className="relative" data-id="element-7293">
              <div className="text-white font-bold text-base mb-0.5" data-id="element-7294">
                Ready to design a better offsite?
              </div>
              <div className="text-slate-300 text-xs" data-id="element-7295">
                Start free, or talk to our pilot team.
              </div>
            </div>
            <div className="relative flex gap-2 shrink-0" data-id="element-7296">
              <button className="bg-brand-coral hover:bg-orange-500 text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors" data-id="element-7297">
                Design Your Free Offsite
              </button>
              <button className="border border-white/30 hover:bg-white/10 text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors" data-id="element-7298">
                Book a Pilot Demo
              </button>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="border-t border-slate-200 px-10 py-5 flex items-center justify-between bg-slate-50" data-id="element-7299">
          <p className="text-[11px] text-slate-600 italic max-w-md" data-id="element-7300">
            Offsite+ helps group experiences become easier to sell, easier to
            run, and easier to justify.
          </p>
          <div className="text-[10px] text-slate-400 font-mono" data-id="element-7301">
            offsiteplus.com
          </div>
        </footer>
      </article>

      {/* Print styles */}
      <style data-id="element-7302">{`
        @media print {
          @page { size: letter; margin: 0; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>;
}